import { format, isValid, parse } from 'date-fns';
import { FieldValidator } from 'final-form';

const getTrimmedStringValue = (value: unknown) =>
  (value ?? '').toString().trim();

export const IS_REQUIRED_ERROR = 'Required';

export const isRequired = (value?: unknown) => {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  const isEmpty = trimmed == null || trimmed === '';
  return isEmpty ? IS_REQUIRED_ERROR : undefined;
};

export const hasAtLeastOneValue = (value?: unknown[]) =>
  !!value?.length &&
  value?.every((v) => (typeof v === 'string' ? !!v.trim() : !!v))
    ? undefined
    : 'Please specify at least one option';

export const hasNoBlankValues = (value?: string[]) =>
  value?.every((v) => !!v.trim().length)
    ? undefined
    : 'There can be no blank values';

export const hasNoCommasInValues = (value?: string[]) =>
  value?.every((v) => !v.includes(','))
    ? undefined
    : 'Options can not contain commas';

export function isNotEmpty<T>(value: T | undefined | null): value is T {
  return getTrimmedStringValue(value) !== '';
}

export const hasUniqueNonEmptyValues = (
  value?: unknown[],
): string | undefined => {
  if (!value || !Array.isArray(value)) {
    return undefined;
  }

  const notEmptyValues = value.filter(isNotEmpty);
  const trimmedNonEmptyValues = notEmptyValues.map(getTrimmedStringValue);

  const alreadySeenValues = new Set<string>();

  for (let i = 0; i < trimmedNonEmptyValues.length; i += 1) {
    const toEvaluate = trimmedNonEmptyValues[i];
    // should always be defined, check mostly for ts
    if (toEvaluate) {
      const isDuplicate = alreadySeenValues.has(toEvaluate);

      if (isDuplicate) {
        return `All values must be unique ("${toEvaluate}" is duplicated)`;
      }

      alreadySeenValues.add(toEvaluate);
    }
  }

  return undefined;
};

export const compose =
  (
    ...validators: (
      | ((value?: unknown) => string | undefined)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      | FieldValidator<any>
    )[]
  ) =>
  (value: unknown, allValues: object) =>
    validators.reduce<string | undefined>(
      (err, validator) => err || validator(value, allValues),
      undefined,
    );

const invalidDateMessage = 'Invalid date format';

export const isDate = (value: unknown) =>
  isValid(value) ? undefined : invalidDateMessage;

export const isDateOrNullish = (value: unknown) =>
  value == null || value === '' ? undefined : isDate(value);

const invalidTimeMessage = 'Invalid time format';

// For AM/PM inputs that convert from military to AM/PM time
const isValidMilitaryTimeString = (value: string) =>
  isValid(parse(value.replace('_', '').trim(), 'HH:mm', new Date()));

export const isTimeStringOrNullish =
  (formatString: string, options: { ampm?: boolean } = {}) =>
  (value: string | null | undefined) =>
    value == null ||
    value === '' ||
    isValid(parse(value, formatString, new Date())) ||
    (options.ampm && isValidMilitaryTimeString(value))
      ? undefined
      : invalidTimeMessage;

export const makeIsAfterDate =
  (minDate: Date | null | undefined) => (value: unknown) => {
    if (!minDate || value == null || value === '') return undefined;
    const date = value instanceof Date ? value : new Date(value as string);
    if (!isValid(date)) return undefined;
    return date > minDate
      ? undefined
      : `Must be after ${format(minDate, 'MM/dd/yyyy')}`;
  };

export const makeIsGreaterThan = (minValue: number) => (value: unknown) => {
  if (value == null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(parsed)) {
    return 'Invalid number';
  }
  return parsed > minValue ? undefined : `Must be greater than ${minValue}`;
};

export const makeIsNotInList =
  (list: unknown[], labelForListItem: string) => (value: unknown) => {
    if (list.map(getTrimmedStringValue).includes(getTrimmedStringValue(value)))
      return `This ${labelForListItem} is already used. Please choose a unique ${labelForListItem}.`;
    return undefined;
  };

export const makeIsNoLongerThan =
  (maxLength: number) => (value: string | null) => {
    if (value && value.length > maxLength)
      return `Must be ${maxLength} or fewer characters.`;
    return undefined;
  };

export const hasNoInvalidCharacters =
  (invalidCharacters: string) => (value: string | null) =>
    invalidCharacters.split('').some((char) => value?.includes(char))
      ? `The following character(s) are not allowed: ${invalidCharacters}`
      : undefined;

export const hasNoWhitespace = (value: string | null) =>
  value && /\s/.test(value) ? 'Cannot contain whitespace' : undefined;
